'use client';

import { useState } from 'react';
import { Search, Package, Calendar, CreditCard, Truck, CheckCircle, XCircle } from 'lucide-react';
import { OrderStatus, Order } from '@/types/orders';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/translations';


export default function OrderLookupPageClient() {
    const [trackingNumber, setTrackingNumber] = useState('');
    const [order, setOrder] = useState<Order | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [message, setMessage] = useState('');
    const { language } = useLanguage();
    const t = useTranslation(language);
    

    const handleSubmit = async () => {
        setSearchError('');
        setOrder(null);
        setIsLoading(true);

        if (!trackingNumber.trim()) {
            setSearchError(t('orders.enterTracking'));
            setIsLoading(false);
            return;
        }

        try {
            const response = await axios.get(`/api/orders/${trackingNumber}`);

            if (response.status === 404) {
                setMessage(t('orders.notFoundText'));
                setIsLoading(false);
                return;
            }
            setOrder(response.data);
        } catch (error) {
            console.error('Error looking up order:', error);
            setSearchError(t('orders.notFoundText'));
            setIsLoading(false);
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusBadge = (status: OrderStatus) => {
        const statusTagClass: Record<OrderStatus, string> = {
            pending: 'tag-neutral',
            processing: 'tag-neutral',
            confirmed: 'tag-accent',
            preparing: 'tag-accent',
            'out-for-delivery': 'tag-accent-2',
            delivered: 'tag-accent-2',
            cancelled: 'tag-outline',
            'failed-delivery': 'tag-outline',
        };

        const statusText: Record<OrderStatus, string> = {
            pending: t('orders.pending'),
            processing: t('orders.processing'),
            confirmed: t('orders.confirmed'),
            preparing: t('orders.preparing'),
            'out-for-delivery': t('orders.outForDelivery'),
            delivered: t('orders.delivered'),
            cancelled: t('orders.cancelled'),
            'failed-delivery': t('orders.failedDelivery'),
        };

        return (
            <span className={`tag ${statusTagClass[status]}`}>
                {statusText[status]}
            </span>
        );
    };

    return (
        <main className="organic min-h-screen">
            <div className="mx-auto" style={{ maxWidth: 760, padding: 'calc(64px + var(--space-8) * 1.2) clamp(20px, 4vw, 48px) calc(var(--space-8) * 2)' }}>
                <div className="text-center" style={{ marginBottom: 'var(--space-6)' }}>
                    <h1 style={{ fontSize: 'clamp(28px, 3.6vw, 38px)', margin: '0 0 var(--space-2)' }}>{t('orders.title')}</h1>
                    <p style={{ color: 'color-mix(in srgb, var(--color-text) 70%, transparent)', margin: 0 }}>
                        {t('orders.subtitle')}
                    </p>
                </div>

                <div className="card elev-sm" style={{ padding: 'var(--space-6)', marginBottom: 'var(--space-6)' }}>
                    <form
                        onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
                        className="flex"
                        style={{ gap: 'var(--space-2)' }}
                    >
                        <label htmlFor="trackingNumber" className="sr-only">{t('orders.trackingNumber')}</label>
                        <input
                            type="text"
                            name="trackingNumber"
                            id="trackingNumber"
                            value={trackingNumber}
                            onChange={(e) => setTrackingNumber(e.target.value)}
                            className="input"
                            style={{ flex: 1 }}
                            placeholder="ex: 37I8-E1Q4-PTYP"
                        />
                        <button type="submit" disabled={isLoading} className="btn btn-primary disabled:opacity-50">
                            <Search className="h-4 w-4" strokeWidth={2.75} />
                            {isLoading ? '...' : t('orders.trackOrder')}
                        </button>
                    </form>
                    {searchError && <p style={{ color: '#b91c1c', fontSize: 13, margin: 'var(--space-2) 0 0' }}>{searchError}</p>}
                </div>

                {order && !isLoading && (
                    <div className="card elev-sm" style={{ padding: 0, overflow: 'hidden' }}>
                        <div
                            className="flex flex-wrap items-center justify-between"
                            style={{ padding: 'var(--space-4) var(--space-6)', borderBottom: '1px solid var(--color-divider)', gap: 'var(--space-2)' }}
                        >
                            <div>
                                <p style={{ margin: 0, fontWeight: 700, fontSize: 17 }}>{t('orders.orderNumber')} #{order.orderId}</p>
                                <p
                                    className="flex items-center gap-1.5"
                                    style={{ margin: '2px 0 0', fontSize: 13, color: 'color-mix(in srgb, var(--color-text) 60%, transparent)' }}
                                >
                                    <Calendar className="h-3.5 w-3.5" strokeWidth={2.75} />
                                    {t('orders.placedOn')} {new Date(order.date).toLocaleDateString('ro-RO')}
                                </p>
                            </div>
                            {getStatusBadge(order.status)}
                        </div>

                        <div style={{ padding: 'var(--space-6)' }}>
                            <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 16 }}>{t('orders.items')}</h3>
                            {order.items.map((item) => (
                                <div
                                    key={item.itemId}
                                    className="flex items-center"
                                    style={{ gap: 'var(--space-3)', paddingBottom: 'var(--space-4)', borderBottom: '1px solid var(--color-divider)', marginBottom: 'var(--space-4)' }}
                                >
                                    <div
                                        className="flex items-center justify-center flex-shrink-0"
                                        style={{ width: 64, height: 64, borderRadius: 'var(--radius-md)', background: 'var(--color-neutral-100)' }}
                                    >
                                        <Package className="h-6 w-6" strokeWidth={2.75} style={{ color: 'var(--color-neutral-500)' }} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p style={{ margin: 0, fontWeight: 600 }}>{item.name}</p>
                                        <p style={{ margin: '2px 0 0', fontSize: 13, color: 'color-mix(in srgb, var(--color-text) 60%, transparent)' }}>
                                            {t('orders.quantity')}: {item.quantity}
                                        </p>
                                    </div>
                                    <span style={{ fontWeight: 600 }}>
                                        {(item.price * item.quantity / 100).toFixed(2)} RON
                                    </span>
                                </div>
                            ))}

                            <div style={{ fontSize: 14, color: 'color-mix(in srgb, var(--color-text) 70%, transparent)' }}>
                                <div className="flex justify-between" style={{ marginBottom: 6 }}>
                                    <span>{t('checkout.subtotal')}</span>
                                    <span>{(order.items.reduce((sum, item) => sum + (item.price * item.quantity / 100), 0)).toFixed(2)} RON</span>
                                </div>
                                <div className="flex justify-between" style={{ marginBottom: 'var(--space-3)' }}>
                                    <span>{t('checkout.shipping')}</span>
                                    <span>{(order.shippingCost / 100).toFixed(2)} RON</span>
                                </div>
                                {(order.discount && order.discount.amount && order.discount.amount > 0) ? (
                                    <div className="flex justify-between" style={{ marginBottom: 'var(--space-3)', color: 'var(--color-accent-700)' }}>
                                        <span>{t('discount.discount')} ({order.discount.code})</span>
                                        <span>-{(order.discount.amount / 100).toFixed(2)} RON</span>
                                    </div>
                                ) : null}
                                <div
                                    className="flex justify-between"
                                    style={{ fontWeight: 700, fontSize: 16, color: 'var(--color-text)', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--color-divider)' }}
                                >
                                    <span>{t('checkout.total')}</span>
                                    <span>{(order.total / 100).toFixed(2)} RON</span>
                                </div>
                            </div>

                            <div style={{ marginTop: 'var(--space-6)', paddingTop: 'var(--space-6)', borderTop: '1px solid var(--color-divider)' }}>
                                <h3 style={{ margin: '0 0 var(--space-4)', fontSize: 16 }}>Informații</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--space-4)' }}>
                                    <div>
                                        <p style={{ margin: '0 0 var(--space-2)', fontWeight: 600, fontSize: 13 }}>Informații de livrare</p>
                                        <div style={{ fontSize: 13.5, color: 'color-mix(in srgb, var(--color-text) 65%, transparent)', lineHeight: 1.6 }}>
                                            <p style={{ margin: 0 }}>{order.shipping.name}</p>
                                            <p style={{ margin: 0 }}>{order.shipping.address}</p>
                                            <p style={{ margin: 0 }}>{order.shipping.city}, {order.shipping.state} {order.shipping.postalCode}</p>
                                            <p style={{ margin: 0 }}>{order.shipping.country}</p>
                                            <p style={{ margin: '8px 0 0' }}>{order.shipping.phone}</p>
                                            <p style={{ margin: 0 }}>{order.shipping.email}</p>
                                            {order.shipping.deliveryDate && (
                                                <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--color-divider)' }}>
                                                    <p style={{ margin: 0, fontWeight: 600, color: 'var(--color-text)' }}>Data și ora preferată pentru livrare:</p>
                                                    <p style={{ margin: '4px 0 0' }}>{new Date(order.shipping.deliveryDate).toLocaleString('ro-RO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                                                </div>
                                            )}
                                            {order.shipping.deliveryInstructions && (
                                                <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--color-divider)' }}>
                                                    <p style={{ margin: 0, fontWeight: 600, color: 'var(--color-text)' }}>Informații suplimentare:</p>
                                                    <p style={{ margin: '4px 0 0' }}>{order.shipping.deliveryInstructions}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {order.billing && (
                                        <div>
                                            <p style={{ margin: '0 0 var(--space-2)', fontWeight: 600, fontSize: 13 }}>Informații de facturare</p>
                                            <div style={{ fontSize: 13.5, color: 'color-mix(in srgb, var(--color-text) 65%, transparent)', lineHeight: 1.6 }}>
                                                <p style={{ margin: 0 }}>{order.billing.name}</p>
                                                <p style={{ margin: 0 }}>{order.billing.address}</p>
                                                <p style={{ margin: 0 }}>{order.billing.city}, {order.billing.state} {order.billing.postalCode}</p>
                                                <p style={{ margin: 0 }}>{order.billing.country}</p>
                                                <p style={{ margin: 0 }}>{order.billing.phone}</p>
                                                <p style={{ margin: 0 }}>{order.billing.email}</p>
                                            </div>
                                        </div>
                                    )}
                                    <div>
                                        <p style={{ margin: '0 0 var(--space-2)', fontWeight: 600, fontSize: 13 }}>{t('checkout.shipping')} Method</p>
                                        <div className="flex items-center gap-2">
                                            <Truck className="h-4 w-4" strokeWidth={2.75} style={{ color: 'var(--color-neutral-500)' }} />
                                            <span style={{ fontSize: 13.5, color: 'color-mix(in srgb, var(--color-text) 65%, transparent)' }}>
                                                Standard {t('checkout.shipping')}
                                            </span>
                                        </div>
                                        {order.status === 'out-for-delivery' && order.trackingNumber && (
                                            <div style={{ marginTop: 'var(--space-3)' }}>
                                                <p style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 13 }}>Tracking Information</p>
                                                <p style={{ margin: 0, fontSize: 13.5, color: 'var(--color-accent)' }}>{order.trackingNumber}</p>
                                                <p style={{ margin: '2px 0 0', fontSize: 12, color: 'color-mix(in srgb, var(--color-text) 55%, transparent)' }}>
                                                    Track your package on the carrier&apos;s website
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginTop: 'var(--space-6)', paddingTop: 'var(--space-6)', borderTop: '1px solid var(--color-divider)' }}>
                                <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 16 }}>{t('orders.paymentInfo')}</h3>
                                <div className="flex items-start gap-3">
                                    <CreditCard className="h-5 w-5 flex-shrink-0" strokeWidth={2.75} style={{ color: 'var(--color-neutral-500)' }} />
                                    <div>
                                        <p style={{ margin: 0, fontWeight: 600, fontSize: 13.5 }}>
                                            {order.payment.method === 'credit-card' && 'Credit Card'}
                                            {order.payment.method === 'bank-transfer' && 'Bank Transfer'}
                                            {order.payment.method === 'cash-on-delivery' && 'Cash on Delivery'}
                                        </p>
                                        {order.payment.status === 'paid' ? (
                                            <span className="flex items-center gap-1" style={{ fontSize: 13.5, color: 'var(--color-accent-2-700)', marginTop: 4 }}>
                                                <CheckCircle className="h-3.5 w-3.5" strokeWidth={2.75} />
                                                Paid on {new Date(order.date).toLocaleDateString('ro-RO')}
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-1" style={{ fontSize: 13.5, color: '#b91c1c', marginTop: 4 }}>
                                                <XCircle className="h-3.5 w-3.5" strokeWidth={2.75} />
                                                {order.payment.status.charAt(0).toUpperCase() + order.payment.status.slice(1)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {isLoading && !order && (
                    <div className="text-center" style={{ padding: 'var(--space-8) 0' }}>
                        <div
                            className="animate-spin rounded-full mx-auto"
                            style={{ width: 48, height: 48, borderTop: '2px solid var(--color-accent)', borderBottom: '2px solid var(--color-accent)' }}
                        />
                        <p style={{ marginTop: 'var(--space-3)', fontSize: 14, color: 'color-mix(in srgb, var(--color-text) 65%, transparent)' }}>
                            Loading order information...
                        </p>
                    </div>
                )}

                {message && !isLoading && (
                    <div className="text-center" style={{ padding: 'var(--space-6) 0' }}>
                        <p style={{ fontSize: 14, color: 'color-mix(in srgb, var(--color-text) 65%, transparent)' }}>{message}</p>
                    </div>
                )}

                {!order && !isLoading && !message && (
                    <div className="card elev-sm text-center" style={{ padding: 'var(--space-8)' }}>
                        <Package
                            className="mx-auto"
                            strokeWidth={2}
                            style={{ width: 48, height: 48, color: 'color-mix(in srgb, var(--color-text) 40%, transparent)', marginBottom: 'var(--space-2)' }}
                        />
                        <h3 style={{ margin: '0 0 4px' }}>
                            {searchError ? 'Eroare' : 'Nicio comandă selectată'}
                        </h3>
                        <p style={{ margin: 0, fontSize: 14, color: searchError ? '#b91c1c' : 'color-mix(in srgb, var(--color-text) 60%, transparent)' }}>
                            {searchError || 'Introdu numărul de comandă mai sus pentru a vedea detaliile.'}
                        </p>
                    </div>
                )}
            </div>
        </main>
    );
}